import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "Shopping Guide", href: "/oopbuy-spreadsheet-guide" },
  { label: "What Is", href: "/#what-is" },
  { label: "How It Works", href: "/#how-it-works" },
  { label: "Comparisons", href: "/#comparison" },
  { label: "Safety", href: "/#safety" },
  { label: "Articles", href: "/#content-hub" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
    setMobileCategoryOpen(false);
  }, [location]);

  const scrollToSection = (href: string) => {
    setMobileOpen(false);
    if (href.startsWith("/#")) {
      const id = href.replace("/#", "");
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/98 backdrop-blur-md border-b border-background-200/70"
          : "bg-background-50/80 backdrop-blur-sm border-b border-background-200/40"
      }`}
    >
      <div className="w-full px-3 md:px-6">
        <div className="flex items-center justify-between h-14 md:h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 shrink-0"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <div className="w-8 h-8 flex items-center justify-center bg-primary-500 rounded-md">
              <i className="ri-table-line text-background-50 text-lg"></i>
            </div>
            <span className="font-heading text-base md:text-lg font-semibold text-foreground-900 whitespace-nowrap">
              Oopbuy Spreadsheet
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((link) => {
              const isPageLink = !link.href.startsWith("/#") && link.href !== "/";
              const isHomeLink = link.href === "/";
              return isPageLink ? (
                <Link
                  key={link.label}
                  to={link.href}
                  className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {link.label}
                </Link>
              ) : isHomeLink ? (
                <Link
                  key={link.label}
                  to={link.href}
                  className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.label}
                  onClick={() => scrollToSection(link.href)}
                  className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {link.label}
                </button>
              );
            })}

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1 ${
                  dropdownOpen
                    ? "bg-primary-50 text-primary-700"
                    : "text-primary-600 hover:text-primary-700 hover:bg-primary-50"
                }`}
              >
                Categories
                <i
                  className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-72 bg-background-50 border border-background-200/80 rounded-xl shadow-lg py-2 max-h-[70vh] overflow-y-auto z-[60]">
                  <p className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                    Shop by Category
                  </p>
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-primary-50 transition-colors cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className="w-8 h-8 flex items-center justify-center bg-background-100 rounded-lg shrink-0">
                        <i className={`${cat.icon} text-sm text-primary-500`}></i>
                      </div>
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-2">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 rounded-full border border-background-300 hover:border-foreground-300 transition-all whitespace-nowrap cursor-pointer"
            >
              Shop Now
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-all whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-background-50 border-t border-background-200/70 py-3 max-h-[80vh] overflow-y-auto">
            {/* Primary Nav Links */}
            <div className="flex flex-col gap-0.5 px-2">
              {navLinks.map((link) => {
                const isPageLink = !link.href.startsWith("/#") && link.href !== "/";
                const isHomeLink = link.href === "/";
                return isPageLink ? (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="px-3 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                  >
                    {link.label === "Shopping Guide" && (
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-book-open-line text-primary-500 text-xs"></i>
                      </div>
                    )}
                    {link.label === "Home" && (
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-home-line text-primary-500 text-xs"></i>
                      </div>
                    )}
                    {link.label}
                  </Link>
                ) : isHomeLink ? (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="px-3 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-home-line text-primary-500 text-xs"></i>
                    </div>
                    {link.label}
                  </Link>
                ) : (
                  <button
                    key={link.label}
                    onClick={() => scrollToSection(link.href)}
                    className="px-3 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors text-left whitespace-nowrap cursor-pointer"
                  >
                    {link.label}
                  </button>
                );
              })}
            </div>

            {/* Mobile Categories */}
            <div className="mt-3 border-t border-background-200/60 pt-3 px-2">
              <button
                onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)}
                className="w-full flex items-center justify-between px-3 py-3 text-sm font-semibold text-primary-600 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-grid-line text-xs"></i>
                  </div>
                  Shop Categories
                </span>
                <i
                  className={`ri-arrow-down-s-line transition-transform duration-200 ${
                    mobileCategoryOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>
              {mobileCategoryOpen && (
                <div className="mt-1 grid grid-cols-2 gap-0.5 pb-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-600 hover:text-foreground-950 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <div className="w-6 h-6 flex items-center justify-center bg-background-100 rounded shrink-0">
                        <i className={`${cat.icon} text-xs text-primary-500`}></i>
                      </div>
                      <span className="truncate">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile CTAs */}
            <div className="mt-3 border-t border-background-200/60 pt-3 px-2 flex flex-col gap-2">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="w-full py-3 text-sm font-medium text-center text-foreground-700 border border-background-300 rounded-full transition-all cursor-pointer"
              >
                Shop Now
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="w-full py-3 text-sm font-semibold text-center text-background-50 bg-primary-500 rounded-full transition-all cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}